// src/components/NotificationDashboard.jsx
import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown, Filter, Bell, CheckSquare } from "lucide-react";
import { NotificationList } from "./notification-list";
import { Pagination } from "./pagination";
import { toast } from "react-toastify";
import {
  getNotifications,
  markAllAsRead,
} from "../../../../Services/notificationService";
import { AiOutlineConsoleSql } from "react-icons/ai";
import { useSelector } from "react-redux";

export function NotificationDashboard() {
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("newest");
  const [isItemsPerPageOpen, setIsItemsPerPageOpen] = useState(false);
  const [isSortOrderOpen, setIsSortOrderOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [filterType, setFilterType] = useState("all");
  const [filterRead, setFilterRead] = useState("all");

  const itemsPerPageRef = useRef(null);
  const sortOrderRef = useRef(null);
  const filterRef = useRef(null);

  const handleNewNotification = (data) => {
    if (data.type === "NEW_NOTIFICATION") {
      if (currentPage === 1 && sortOrder === "newest") {
        setNotifications((prev) => [data.data, ...prev]);
        setTotalItems((prev) => prev + 1);
      } else {
        toast.success(`New notification: ${data.data.title}`);
      }
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [
    currentPage,
    itemsPerPage,
    sortOrder,
    filterType,
    filterRead,
    searchQuery,
  ]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort: sortOrder === "newest" ? "-createdAt" : "createdAt",
      };

      if (filterType !== "all") {
        params.resourceType = filterType.toUpperCase();
      }

      if (filterRead !== "all") {
        params.read = filterRead === "read";
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await getNotifications(params);
      setNotifications(response.data);
      setTotalItems(response.pagination.total);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setLoading(false);
      toast.error("Failed to load notifications");
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        itemsPerPageRef.current &&
        !itemsPerPageRef.current.contains(event.target)
      ) {
        setIsItemsPerPageOpen(false);
      }
      if (
        sortOrderRef.current &&
        !sortOrderRef.current.contains(event.target)
      ) {
        setIsSortOrderOpen(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    setIsItemsPerPageOpen(false);
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    setIsSortOrderOpen(false);
    setCurrentPage(1);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setCurrentPage(1);
  };

  const handleFilterReadChange = (value) => {
    setFilterRead(value);
    setCurrentPage(1);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success("All notifications marked as read");
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleRefresh = () => {
    fetchNotifications();
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setCurrentPage(1);
      fetchNotifications();
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="p-4 bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center">
          <Bell className="mr-2 h-5 w-5 text-blue-500" />
        </h2>
        <div className="flex space-x-2">
          <button
            className="px-3 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200 flex items-center"
            onClick={handleRefresh}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          {/* <button
            className="px-3 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200 flex items-center"
            onClick={handleMarkAllAsRead}
          >
            <CheckSquare className="h-4 w-4 mr-1" />
            Mark All as Read
          </button> */}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search in notifications..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearch}
          />
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="relative" ref={filterRef}>
            <button
              className="flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter className="h-4 w-4 mr-2 text-gray-500" />
              <span>
                {filterType === "all"
                  ? "All Types"
                  : filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                {filterRead !== "all" &&
                  ` • ${filterRead === "read" ? "Read" : "Unread"}`}
              </span>
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>
            {isFilterOpen && (
              <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg">
                <div className="p-3 border-b border-gray-200">
                  <h4 className="font-medium text-sm mb-2">Type</h4>
                  <div className="space-y-1">
                    {["all", "form", "material", "user", "admin"].map(
                      (type) => (
                        <div
                          key={type}
                          className={`px-2 py-1 rounded-md text-sm cursor-pointer transition-colors ${
                            filterType === type
                              ? "bg-blue-100 text-blue-700"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => handleFilterTypeChange(type)}
                        >
                          {type === "all"
                            ? "All Types"
                            : type.charAt(0).toUpperCase() + type.slice(1)}
                        </div>
                      )
                    )}
                  </div>
                </div>
                {/* <div className="p-3">
                  <h4 className="font-medium text-sm mb-2">Status</h4>
                  <div className="space-y-1">
                    {["all", "read", "unread"].map((status) => (
                      <div
                        key={status}
                        className={`px-2 py-1 rounded-md text-sm cursor-pointer transition-colors ${
                          filterRead === status
                            ? "bg-blue-100 text-blue-700"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleFilterReadChange(status)}
                      >
                        {status === "all"
                          ? "All Status"
                          : status.charAt(0).toUpperCase() + status.slice(1)}
                      </div>
                    ))}
                  </div>
                </div> */}
              </div>
            )}
          </div>
          <div className="relative" ref={sortOrderRef}>
            <button
              className="flex items-center justify-between w-[140px] px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              onClick={() => setIsSortOrderOpen(!isSortOrderOpen)}
            >
              <span>
                {sortOrder === "newest" ? "Newest first" : "Oldest first"}
              </span>
              <ChevronDown className="h-4 w-4 ml-2" />
            </button>
            {isSortOrderOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                <ul className="py-1">
                  <li
                    className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                      sortOrder === "newest" ? "bg-blue-50 text-blue-700" : ""
                    }`}
                    onClick={() => handleSortChange("newest")}
                  >
                    Newest first
                  </li>
                  <li
                    className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                      sortOrder === "oldest" ? "bg-blue-50 text-blue-700" : ""
                    }`}
                    onClick={() => handleSortChange("oldest")}
                  >
                    Oldest first
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          <NotificationList
            notifications={notifications}
            onRefresh={fetchNotifications}
          />

          {notifications.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-2" ref={itemsPerPageRef}>
                <span className="text-sm text-gray-500">Show</span>
                <div className="relative">
                  <button
                    className="flex items-center justify-between w-[70px] px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    onClick={() => setIsItemsPerPageOpen(!isItemsPerPageOpen)}
                  >
                    <span>{itemsPerPage}</span>
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </button>
                  {isItemsPerPageOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                      <ul className="py-1">
                        {[10, 25, 50, 100].map((value) => (
                          <li
                            key={value}
                            className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                              itemsPerPage === value
                                ? "bg-blue-50 text-blue-700"
                                : ""
                            }`}
                            onClick={() => handleItemsPerPageChange(value)}
                          >
                            {value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-500">per page</span>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          ) : searchQuery || filterType !== "all" || filterRead !== "all" ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="inline-flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Bell className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 text-lg mb-1">
                No notifications match your filters
              </p>
              <p className="text-gray-500 text-sm mb-4">
                Try changing your search or filters to find what you're looking
                for.
              </p>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
                onClick={() => {
                  setSearchQuery("");
                  setFilterType("all");
                  setFilterRead("all");
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="inline-flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <CheckSquare className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 text-lg mb-1">
                You're all caught up!
              </p>
              <p className="text-gray-500 text-sm">
                No notifications to display right now.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
